'use client';

import type { SnackbarCloseReason, SnackbarProps } from '@mui/material';
import { Box, ButtonBase, Snackbar, SnackbarContent } from '@mui/material';
import {
  Base,
  Subheading
} from '@natomalabs/natomui/src/components/Typography';
import React, { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';
import {
  AlertCircleIcon,
  CheckCircleIcon,
  CloseCircleIcon,
  CloseIcon,
  InfoIcon
} from '@natomalabs/natomui/src/icons';
import theme from '@natomalabs/natomui/src/theme/theme';

export type ToastContextType = {
  /**
   * Displays a Toast in the lower left corner of the screen.
   *
   * @param message The primary message of the Toast to be displayed.
   * @param props Additional props to be passed to the Snackbar content.
   */
  showToast: (
    message: ReactNode | string,
    props?: Partial<ExtendableToastProps>
  ) => void;

  /**
   * Hides the current displayed Toast.
   */
  hideToast: (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => void;
};

export const ToastContext = createContext<ToastContextType>({
  showToast: function () {},
  hideToast: function () {}
});

export type ToastVariant = 'success' | 'warning' | 'error' | 'info';

export type ExtendableToastProps = Omit<
  SnackbarProps,
  'open' | 'onClose' | 'ref' | 'message' | 'children'
> & { description?: ReactNode | string | undefined; variant: ToastVariant };

function IconForVariant({ variant }: { variant?: ToastVariant }): ReactNode {
  // 1px nudge to offset the icon by its frame
  switch (variant) {
    case 'success':
      return (
        <CheckCircleIcon
          width={24}
          height={24}
          color={theme.palette.green[500]}
        />
      );
    case 'error':
      return (
        <CloseCircleIcon
          width={24}
          height={24}
          color={theme.palette.red[500]}
        />
      );
    case 'warning':
      return (
        <AlertCircleIcon
          width={24}
          height={24}
          color={theme.palette.amber[500]}
        />
      );
    default:
      return (
        <InfoIcon width={24} height={24} color={theme.palette.blue[500]} />
      );
  }
}

/**
 * Provider to unify Toast management throughout the application.
 *
 * @param props
 * @param props.children Child content for provider
 * @returns
 */
export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [subject, setSubject] = useState<ReactNode | string | undefined>();
  const [description, setDescription] = useState<
    ReactNode | string | undefined
  >();
  const [variant, setVariant] = useState<ToastVariant | undefined>();
  const [props, setProps] = useState<Partial<ExtendableToastProps> | undefined>(
    {}
  );

  const showToast = (
    subject: ReactNode | string,
    { description, variant, ...props }: Partial<ExtendableToastProps> = {}
  ) => {
    setSubject(subject);
    setProps(props);
    setDescription(description);
    setVariant(variant);
  };

  const hideToast = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') return;
    setSubject(undefined);
    setDescription(undefined);
    setProps(undefined);
    setVariant(undefined);
  };

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
      <Box sx={{ flexShrink: 0, height: 24 }}>
        <IconForVariant variant={variant} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Subheading
          sx={(theme) => ({
            color: theme.palette.neutral[850],
            fontWeight: 'semibold'
          })}
        >
          {subject}
        </Subheading>
        {description && (
          <Base sx={(theme) => ({ color: theme.palette.neutral[600] })}>
            {description}
          </Base>
        )}
      </Box>
    </Box>
  );

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      {subject && (
        <Snackbar open={!!subject} onClose={hideToast} {...props}>
          <SnackbarContent
            variant={variant}
            message={content}
            action={
              <ButtonBase
                onClick={hideToast}
                sx={(theme) => ({
                  marginRight: theme.spacing(1),
                  color: theme.palette.neutral[600],
                  '&:hover': {
                    color: theme.palette.neutral[700]
                  }
                })}
              >
                <CloseIcon width={24} height={24} />
              </ButtonBase>
            }
          />
        </Snackbar>
      )}
    </ToastContext.Provider>
  );
};

export default function useToast() {
  return useContext(ToastContext);
}
